import React, { useEffect, useState } from "react";
import { useSearchParams, useLocation } from "react-router-dom";
import "./summerProgram2026.css";
import axiosClient from "../axiosClient";
import SummerProgramRegistrationForm from "./summerProgramRegistrationForm";
import bakeryImage from "./images/2026SummerBakery.jpg";
import calculusImage from "./images/2026SummerCalculus.jpg";
import geometryImage from "./images/2026SummerGeometry.jpg";
import machineryStage1Image from "./images/2026SummerMachinery-stage1.jpg";
import machineryStage2Image from "./images/2026SummerMachinery-stage2.jpg";
import machineryStage3Image from "./images/2026SummerMachinery-stage3.jpg";
import machineryStage4Image from "./images/2026SummerMachinery-stage4.jpg";

const WELCOME_PAGE = {
  id: "welcome",
  pageTitle: "Welcome to 2026 SVT Summer Programs",
  sections: [
    "Give your student a productive and rewarding summer with Seattle Voctech High School's 2026 Summer Programs! Students can get ahead academically through our Calculus Preview and Geometry Preview courses, designed to strengthen mathematical foundations, build confidence, and prepare for success in advanced coursework.",
    "For hands-on learners, Introduction to Machinery provides practical exposure to tools, equipment, and manufacturing concepts, while Introduction to Bakery introduces students to baking techniques, kitchen safety, and food preparation skills in an engaging learning environment.",
    "These interactive programs help students develop valuable academic and career-ready skills, gain real-world experience, and explore future interests while building a competitive edge for high school, college, and beyond.",
  ],
};

const PROGRAMS = [
  {
    id: "program-1",
    title: "Calculus Preview",
    introduction: `Time: 6/29 – 7/24, 9:30am – 12:00pm every Mon, Wed and Fri. Total 30 class hours
Venue: Seattle Voctech High School, 15044 SE 44th Pl, Bellevue, WA 98006
Size: min 4 to max 6 students who are going to take Calculus in new school year, or with related needs
Cost: $999 per student

Host by Dr. Helen:
- Master’s in mathematics education with a PhD in Electronics Engineering
- Certified Mathematics Teacher in Washington State and Certified IB Mathematics Teacher
- Extensive teaching experience in both private and public high schools
- Taught a wide range of courses including Geometry, Algebra, Precalculus, AP Calculus and Statistics
`,
    image: calculusImage,
    introductionAfterImage: `
Lesson 1-2 Limit:
• Finding Limits Graphically and Numerically
• Evaluating Limits Analytically
• Continuity and One-Sided Limits

Lesson 3-5 Differentiation:
• The Derivative and the Tangent Line Problem
• Basic Differentiation Rules and Rates of Change
• Product and Quotient Rules and Higher-Order Derivatives
• The Chain Rule
• Implicit Differentiation

Lesson 6-8 Applications of Differentiation:
• Extrema on an Interval
• Rolle's Theorem and the Mean Value Theorem
• Increasing and Decreasing Functions and the First Derivative Test
• Concavity and the Second Derivative Test

Lesson 9-12 Integration:
• Antiderivatives and Indefinite Integration
• Riemann Sums and Definite Integrals
• The Fundamental Theorem of Calculus
• Integration by Substitution`,
  },
  {
    id: "program-2",
    title: "Geometry Preview",
    introduction: `Time: 6/29 – 7/10, 1:30pm – 3:30pm Monday through Friday. Total 20 class hours
Venue: Seattle Voctech High School, 15044 SE 44th Pl, Bellevue, WA 98006
Size: min 4 to max 6 students who are going to take Geometry in new school year, or with related needs
Cost: $666 per student

Host by Dr. Helen:
- Master's in mathematics education with a PhD in Electronics Engineering
- Certified Mathematics Teacher in Washington State and Certified IB Mathematics Teacher
- Extensive teaching experience in both private and public high schools
- Taught a wide range of courses including Geometry, Algebra, Precalculus, AP Calculus and Statistics
`,
    image: geometryImage,
    introductionAfterImage: `
Lesson 1-3 Parallel Lines and Proofs
Triangle Congruence and proofs
Similar Triangles and proofs

Lesson 4-6 Triangle Theorems
Isosceles triangle theorem and its converse,
Medians and altitudes of triangles
Angle bisectors, perpendicular bisectors, and mid segments of triangles

Lesson 7-10 Right triangles
Pythagorean theorem
Similar right triangles
Special right triangles
Trig ratios`,
  },
  {
    id: "program-3",
    title: "Bakery",
    pageTitle: "Introduction to Bakery",
    introduction: `
Time: 7/13 – 7/23, 9:30am – 12:00pm Mon–Thu. Total 20 class hours
Venue: Seattle Voctech High School, 15044 SE 44th Pl, Bellevue, WA 98006
Size: min 4 to max 6 students
Cost: $666 per student

Instructor: Joan Ferry
- Teaching CTE-Cooking classes at SVT
- Worked at Albertsons for 40 years as a cake decorator/ Bakery manager
- Also a vendor at a few craft fairs selling my baked goods.
- Lived in the Bellevue area for 40 years and raised 2 girls in Bellevue school district`,
    courseGoals: `By the end of the course, students will be able to:
●	Practice safe kitchen habits
●	Measure ingredients accurately
●	Understand basic baking science
●	Follow recipes independently
●	Use common baking tools correctly
●	Demonstrate teamwork and cleanup responsibility
●	Create several baked goods from scratch`,
    image: bakeryImage,
    scheduleLayout: "bakery",
  },
  {
    id: "program-4",
    title: "Machinery",
    pageTitle: "Introduction to Mechanical and Electrical Processes",
    introLayout: "stageRows",
    introduction: `
Time: 7/13 – 7/24, 1:30pm – 4:00pm Mon–Fri. Total 25 class hours
Venue: Seattle Voctech High School, 15044 SE 44th Pl, Bellevue, WA 98006
Size: min 4 to max 6 students
Cost: $777 per student

Instructor: Zachary VanTassel
- A human of some talent but more curiosity
- Eight years’ history teaching experience
- Two years’ automotive technician experience, specializing in the DeLorean DMC12
- Hobbies include using primary sources to convince students to move to Waco, Texas in 1884
- Excellent sense of humor

Do you want to work with your hands and your head? Learn to build things this summer!
No experience required!
Build devices using premade kits and then design your own using electrical components.
Great way to experience a new hobby or explore a profession.`,
    stageRows: [
      {
        text: "Stage 1: Students will create small mechanical and electrical models from kits to develop skills with small tools and practice reading and following basic instructions to create a finished product.",
        image: machineryStage1Image,
      },
      {
        text: "Stage 2: Students will create small electrical models, using kits, that develop skills with a soldering iron. Included is an introduction into the basics of electricity and how it powers small devices.",
        image: machineryStage2Image,
      },
      {
        text: "Stage 3: Students will create small electrical devices using only electrical components. This will develop basic skills for reading electrical diagrams and a basic understanding of electrical components.",
        image: machineryStage3Image,
      },
      {
        text: "Stage 4: Students will create small electrical devices that they design and build using the knowledge they developed over the course.",
        image: machineryStage4Image,
      },
    ],
  },
  {
    id: "program-5",
    title: "Free Planning",
    pageTitle: "Free College Planning Assessment",
    isFreeRegistration: true,
    introLayout: "assessment",
    tagline: "Plan early. Apply with confidence.",
    introduction: `Drawing upon 15 years of successful college planning case studies from our institution, our experienced planning mentors provide personalized diagnostic assessments and practical recommendations for students and families.

Each mentor brings over eight years of college planning experience and will help evaluate the student’s academic background, extracurricular profile, target schools, application timeline, and next-step strategy.`,
    assessmentIncludes: [
      "Academic and college readiness review",
      "College application timeline guidance",
      "Major and school selection suggestions",
      "Extracurricular and activity planning feedback",
      "Personalized next-step recommendations",
    ],
    appointmentSchedule:
      "Every Saturday from June 27 through August 1",
    appointmentSessions: ["9:30 AM – 10:30 AM", "11:00 AM – 12:00 PM"],
    reserveText:
      "Spaces are limited. Schedule your appointment today and take the first step toward a stronger college application plan.",
  },
];

const PAID_DIALOG_MESSAGE =
  "Thank you for your payment. Your registration is complete. See you in class.";

function ScheduleLabel({ children }) {
  return <p className="summer-program-schedule-label">{children}</p>;
}

function ScheduleList({ items }) {
  return (
    <ul className="summer-program-schedule-list">
      {items.map((item) => (
        <li key={item}>{item}</li>
      ))}
    </ul>
  );
}

function renderBakeryClassSchedule() {
  return (
    <div className="summer-program-bakery-schedule">
      <p className="summer-program-schedule-heading">
        <strong>Class Schedule</strong>
      </p>

      <p className="summer-program-schedule-week">
        <strong>Week 1</strong>
      </p>
      <table
        className="summer-program-schedule-table summer-program-intro-table"
        role="presentation"
      >
        <tbody>
          <tr>
            <td>
              <ScheduleLabel>
                <strong>Day 1</strong>
              </ScheduleLabel>
              <ScheduleLabel>
                <strong>Kitchen Safety</strong>
              </ScheduleLabel>
              <ScheduleList
                items={[
                  "Basic Safety (Oven and Tool Safety)",
                  "Hand Washing and Sanitation",
                  "Reading recipes",
                  "Measuring dry vs Wet (Importance of precise measurements)",
                  "Intro to Baking Vocab",
                ]}
              />
              <ScheduleLabel>
                <strong>Activity</strong>
              </ScheduleLabel>
              <p>Measurement Relay Game</p>
              <ScheduleLabel>
                <strong>Bake</strong>
              </ScheduleLabel>
              <p>Chocolate Chip Cookies</p>
              <ScheduleLabel>
                <strong>Homework</strong>
              </ScheduleLabel>
              <p>Decorate Journal</p>
            </td>
            <td>
              <ScheduleLabel>
                <strong>Day 2</strong>
              </ScheduleLabel>
              <ScheduleLabel>
                <strong>Mixing Methods &amp; Muffins</strong>
              </ScheduleLabel>
              <ScheduleList
                items={[
                  "Muffin method",
                  "Avoiding overmixing",
                  "Proper scooping techniques",
                  "Difference between Stirring, Folding, Beating, Creaming",
                ]}
              />
              <ScheduleLabel>
                <strong>Bake</strong>
              </ScheduleLabel>
              <p>Blueberry or Banana Muffins</p>
            </td>
            <td>
              <ScheduleLabel>
                <strong>Day 3</strong>
              </ScheduleLabel>
              <ScheduleLabel>
                <strong>Brownies &amp; Bar Cookies</strong>
              </ScheduleLabel>
              <ScheduleList
                items={[
                  "Mixing Batter evenly",
                  "Proper pan prep",
                  "Butter vs Oil",
                  "Role of eggs and sugar Kneading dough",
                ]}
              />
              <ScheduleLabel>
                <strong>Bake</strong>
              </ScheduleLabel>
              <p>Fudge Brownies</p>
              <p>How timing changes texture- gooey vs cakey</p>
              <ScheduleLabel>
                <strong>Activity</strong>
              </ScheduleLabel>
              <p>Vote for favorite brownie texture and best mix-ins</p>
            </td>
            <td>
              <ScheduleLabel>
                <strong>Day 4</strong>
              </ScheduleLabel>
              <ScheduleLabel>
                <strong>Pie and Pastry Day</strong>
              </ScheduleLabel>
              <ScheduleList
                items={[
                  "Cutting butter into flour",
                  "Rolling dough evenly",
                  "Working with cold ingredients",
                ]}
              />
              <p>What Makes a pastry flaky?</p>
              <ScheduleLabel>
                <strong>Activity</strong>
              </ScheduleLabel>
              <p>Design decorative crusts</p>
              <ScheduleLabel>
                <strong>Bake</strong>
              </ScheduleLabel>
              <p>Hand pies</p>
            </td>
          </tr>
        </tbody>
      </table>

      <p className="summer-program-schedule-week">
        <strong>Week 2</strong>
      </p>
      <table
        className="summer-program-schedule-table summer-program-intro-table"
        role="presentation"
      >
        <tbody>
          <tr>
            <td>
              <ScheduleLabel>
                <strong>Day 5</strong>
              </ScheduleLabel>
              <ScheduleLabel>
                <strong>Baking Cakes &amp; Cupcakes</strong>
              </ScheduleLabel>
              <ScheduleList
                items={[
                  "Cake Mixing Methods",
                  "Testing Doneness Basic Frosting Techniques",
                ]}
              />
              <p>How leavening agents work (Baking Soda and Powder)</p>
              <ScheduleLabel>
                <strong>Bake</strong>
              </ScheduleLabel>
              <p>Cupcakes - vanilla and chocolate</p>
            </td>
            <td>
              <ScheduleLabel>
                <strong>Day 6</strong>
              </ScheduleLabel>
              <ScheduleLabel>
                <strong>Frosting and Decorating</strong>
              </ScheduleLabel>
              <ScheduleList
                items={[
                  "Make frosting from scratch",
                  "Mix and color frosting",
                  "Basic decorating techniques",
                  "Use piping bags and decorating tools",
                  "Types of Frosting (Buttercream, cream cheese, whipped, glaze vs. frosting)",
                ]}
              />
              <ScheduleLabel>
                <strong>Activity</strong>
              </ScheduleLabel>
              <p>Design cupcakes around a theme</p>
            </td>
            <td>
              <ScheduleLabel>
                <strong>Day 7</strong>
              </ScheduleLabel>
              <ScheduleLabel>
                <strong>Baking Challenge &amp; Team Competition</strong>
              </ScheduleLabel>
              <ScheduleList items={["Time Management", "Teamwork"]} />
              <ScheduleLabel>
                <strong>Activity</strong>
              </ScheduleLabel>
              <p>Bake Off</p>
              <p>Judging (Taste, Presentation, Teamwork and Cleanliness)</p>
            </td>
            <td>
              <ScheduleLabel>
                <strong>Day 8</strong>
              </ScheduleLabel>
              <ScheduleLabel>
                <strong>Final Showcase &amp; Celebration</strong>
              </ScheduleLabel>
              <p>Choose one favorite recipe to recreate with minimal assistance</p>
              <ScheduleLabel>
                <strong>Reflection</strong>
              </ScheduleLabel>
              <ScheduleList
                items={[
                  "Favorite Recipe?",
                  "Biggest Challenge?",
                  "Most important skill learned?",
                ]}
              />
              <p>Families welcome!</p>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function renderWelcomePage() {
  return (
    <div className="summer-program-welcome">
      {WELCOME_PAGE.sections.map((paragraph, index) => (
        <p key={`welcome-section-${index + 1}`} className="summer-program-welcome-section">
          {paragraph}
        </p>
      ))}
    </div>
  );
}

function renderFreeAssessmentIntro(program) {
  return (
    <>
      {program.tagline && (
        <p className="summer-program-assessment-tagline">
          <strong>{program.tagline}</strong>
        </p>
      )}
      {program.introduction && (
        <div className="summer-program-intro-text">{program.introduction}</div>
      )}
      {program.assessmentIncludes?.length > 0 && (
        <>
          <p className="summer-program-course-goals-heading">
            <strong>Assessment Includes</strong>
          </p>
          <ScheduleList items={program.assessmentIncludes} />
        </>
      )}
      <p className="summer-program-course-goals-heading">
        <strong>Appointment Times</strong>
      </p>
      <p className="summer-program-assessment-schedule">
        <strong>{program.appointmentSchedule}</strong>
      </p>
      <p className="summer-program-intro-text">Available sessions:</p>
      <ScheduleList items={program.appointmentSessions} />
      <p className="summer-program-intro-text">By appointment only.</p>
      <p className="summer-program-course-goals-heading">
        <strong>Reserve Your Free Assessment</strong>
      </p>
      <p className="summer-program-intro-text">{program.reserveText}</p>
    </>
  );
}

function renderProgramIntro(program) {
  if (program.introLayout === "assessment") {
    return renderFreeAssessmentIntro(program);
  }

  if (program.introLayout === "stageRows" && program.stageRows?.length) {
    return (
      <>
        {program.introduction && (
          <div className="summer-program-intro-text">{program.introduction}</div>
        )}
        <table
          className="summer-program-intro-table summer-program-stage-table"
          role="presentation"
        >
          <tbody>
            {program.stageRows.map((row, index) => (
              <tr key={`${program.id}-stage-${index + 1}`}>
                <td className="summer-program-intro-table-text">
                  <div className="summer-program-intro-text">{row.text}</div>
                </td>
                <td className="summer-program-intro-table-image">
                  <img
                    src={row.image}
                    alt={`${program.title} stage ${index + 1}`}
                    className="summer-program-intro-image summer-program-stage-image"
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </>
    );
  }

  if (program.introLayout === "sideBySide" && program.image) {
    return (
      <>
        <table className="summer-program-intro-table" role="presentation">
          <tbody>
            <tr>
              <td className="summer-program-intro-table-text">
                <div className="summer-program-intro-text">{program.introduction}</div>
              </td>
              <td className="summer-program-intro-table-image">
                <img
                  src={program.image}
                  alt={`${program.title} program`}
                  className="summer-program-intro-image"
                />
              </td>
            </tr>
          </tbody>
        </table>
        {program.introductionAfterImage && (
          <div className="summer-program-intro-text">{program.introductionAfterImage}</div>
        )}
      </>
    );
  }

  return (
    <>
      {program.introduction && (
        <div className="summer-program-intro-text">{program.introduction}</div>
      )}
      {program.courseGoals && (
        <>
          <p className="summer-program-course-goals-heading">
            <strong>Course Goals</strong>
          </p>
          <div className="summer-program-intro-text">{program.courseGoals}</div>
        </>
      )}
      {program.image && (
        <img
          src={program.image}
          alt={`${program.title} program`}
          className="summer-program-intro-image"
        />
      )}
      {program.scheduleLayout === "bakery" && renderBakeryClassSchedule()}
      {program.introductionAfterImage && (
        <div className="summer-program-intro-text">{program.introductionAfterImage}</div>
      )}
    </>
  );
}

function SummerProgram2026() {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const [activeProgramId, setActiveProgramId] = useState(WELCOME_PAGE.id);
  const [programSettings, setProgramSettings] = useState({});
  const [cancellationPolicy, setCancellationPolicy] = useState("");
  const [dialogMessage, setDialogMessage] = useState("");
  const [showDialog, setShowDialog] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isWelcomePage = activeProgramId === WELCOME_PAGE.id;
  const activeProgram =
    PROGRAMS.find((program) => program.id === activeProgramId) || PROGRAMS[0];
  const activeProgramSettings = programSettings[activeProgram.title] || {
    paymentOpen: true,
    isFree: false,
    registrationClosesAtDisplay: "",
  };

  useEffect(() => {
    const loadProgramSettings = async () => {
      try {
        const response = await axiosClient.get("/summerProgramSettings");
        const settingsMap = {};

        response.data.programs.forEach((program) => {
          settingsMap[program.name] = program;
        });

        setProgramSettings(settingsMap);
        setCancellationPolicy(response.data.cancellationPolicy || "");
      } catch (error) {
        console.error("Error loading summer program settings:", error);
      }
    };

    loadProgramSettings();
  }, []);

  useEffect(() => {
    if (location.state?.showWelcome) {
      setActiveProgramId(WELCOME_PAGE.id);
      setShowDialog(false);
    }
  }, [location.key, location.state]);

  useEffect(() => {
    if (searchParams.get("paid") === "1") {
      setActiveProgramId(WELCOME_PAGE.id);
      setDialogMessage(PAID_DIALOG_MESSAGE);
      setShowDialog(true);
      setSearchParams({});
    } else if (searchParams.get("cancelled") === "1") {
      setActiveProgramId(WELCOME_PAGE.id);
      setDialogMessage(
        "Payment was not completed. Please try again later using the payment link in your email."
      );
      setShowDialog(true);
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);

  const handleSubmit = async (formData) => {
    setIsSubmitting(true);

    try {
      const response = await axiosClient.post("/summerProgramRegistrations", formData);
      const { registrationAction } = formData;

      if (registrationAction === "pay_now" && response.data.checkoutUrl) {
        window.location.href = response.data.checkoutUrl;
        return;
      }

      setFormKey((prev) => prev + 1);

      if (registrationAction === "future_interest") {
        setDialogMessage(
          "Thank you for your interest. This program has already started, so payment is no longer available. Seattle Voctech School will contact you with information about future programs."
        );
      } else if (registrationAction === "free_register") {
        setDialogMessage(
          response.data.message ||
            "Thank you for your registration. Our counselor will contact you to schedule a session."
        );
      } else if (registrationAction === "pay_later") {
        setDialogMessage(
          "Thank you for your registration. A payment link has been sent to your email. Seattle Voctech School will contact you with next steps for the 2026 Summer Program."
        );
      } else {
        setDialogMessage(
          "Thank you for your registration. Seattle Voctech School will contact you with next steps for the 2026 Summer Program."
        );
      }

      setShowDialog(true);
    } catch (error) {
      const response = error.response;

      if (response?.status === 409 && response?.data?.full) {
        setDialogMessage(
          "The program is full. We will contact you if seats open again."
        );
        setShowDialog(true);
        return;
      }

      console.error("Error submitting summer program registration:", error);
      setDialogMessage(
        response?.data?.message ||
          "We could not submit your registration. Please try again later."
      );
      setShowDialog(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="summer-program-page">
      <div className="summer-program-container">
        <div className="summer-program-side-nav">
          {PROGRAMS.map((program) => (
            <button
              key={program.id}
              type="button"
              className={
                program.id === activeProgramId
                  ? "summer-program-side-nav-link active"
                  : "summer-program-side-nav-link"
              }
              onClick={() => {
                setActiveProgramId(program.id);
                setShowDialog(false);
              }}
            >
              {program.menuTitle ?? program.title}
            </button>
          ))}
        </div>

        <div className="summer-program-main">
          <h2 className={isWelcomePage ? "summer-program-welcome-title" : undefined}>
            {isWelcomePage
              ? WELCOME_PAGE.pageTitle
              : activeProgram.pageTitle ?? activeProgram.title}
          </h2>
          <div className="summer-program-intro">
            {isWelcomePage ? renderWelcomePage() : renderProgramIntro(activeProgram)}
          </div>

          {!isWelcomePage && (
          <div className="registeration-form-container">
            <h3><b>2026 Summer Program Registration Form</b></h3>
            <h3>Please complete one form for each student to be registered</h3>
            <h3>(* - Required Information)</h3>
            <div className="registeration-form">
              <SummerProgramRegistrationForm
                key={`${activeProgramId}-${formKey}`}
                programName={activeProgram.title}
                paymentOpen={activeProgramSettings.paymentOpen}
                isFreeRegistration={
                  activeProgram.isFreeRegistration || activeProgramSettings.isFree
                }
                registrationClosesAtDisplay={
                  activeProgramSettings.registrationClosesAtDisplay
                }
                cancellationPolicy={cancellationPolicy}
                onSubmit={handleSubmit}
                isSubmitting={isSubmitting}
              />
            </div>
          </div>
          )}
        </div>
      </div>

      {showDialog && (
        <div className="summer-program-success-overlay">
          <div className="summer-program-success-dialog">
            <p>{dialogMessage}</p>
            <button
              type="button"
              className="summer-program-success-close"
              onClick={() => setShowDialog(false)}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default SummerProgram2026;
